import { Anchor, Button, Checkbox, Divider, Group, Radio, rem, Select, Space, Stack, Text, TextInput, Title } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import { useOptInUserFormContext } from "shared/components/opt-in/context";
import { features } from "shared/graphql/features";
import { useFormState } from "shared/hooks/useFormState";
import { titlecase } from "shared/utils/fns";
import { Language } from "src/gql/graphql";

export const RegisterPanel = () => {
    const hasOptInDuration = features.enabled("HAS_OPT_IN_DURATION");

    const form = useOptInUserFormContext();
    const [customer] = useFormState(form, "customer");
    const [step, setStep] = useFormState(form, "meta.step");

    return (
        <form>
            <Stack>
                <div>
                    <Title order={4}>Sign Up for Alerts</Title>
                    <Text size="sm">Sign up to receive alerts from {customer.name}.</Text>
                </div>

                <Select
                    allowDeselect={false}
                    label="Language Preference"
                    required
                    defaultValue={Language.English}
                    withAsterisk
                    data={customer.languages.map((l) => ({ label: titlecase(l as string), value: l }))}
                    w="min-content"
                    miw={rem(300)}
                />

                <TextInput label="Name" {...form.getInputProps("name")} w="min-content" miw={rem(300)} />

                {hasOptInDuration && (
                    <Radio.Group
                        name="duration"
                        label="Duration"
                        description="How long do you want to receive alerts?"
                        withAsterisk
                        {...form.getInputProps("durationDays")}
                    >
                        <Group mt="xs">
                            <Radio label="3 Days" value={"3"}></Radio>
                            <Radio label="7 Days" value={"7"}></Radio>
                            <Radio label="30 Days" value={"30"}></Radio>
                            <Radio label="Doesn't Expire" value={"forever"}></Radio>
                        </Group>
                    </Radio.Group>
                )}

                <Space />

                <Checkbox
                    {...form.getInputProps("agreedToPrivacyPolicy", { type: "checkbox" })}
                    label={
                        <Text size="sm">
                            By checking this box, you agree to receive SMS/text messages, phone calls, and/or emails from{" "}
                            <strong>{customer.name}</strong>, sent by Bryx, Inc. on their behalf, for the purpose of delivering emergency
                            alerts, service notifications, and important announcements. Message frequency may vary. Message and data rates
                            may apply. You can opt out of SMS messages at any time by replying STOP. For help, reply HELP. For more details,
                            please review our{" "}
                            <Anchor href="https://bryx.com/privacy-policy-1" c="primary" target="_blank">
                                Privacy Policy
                            </Anchor>
                            .
                        </Text>
                    }
                />

                <Checkbox
                    {...form.getInputProps("agreedToTOS", { type: "checkbox" })}
                    label={
                        <Text size="sm">
                            By creating this account you agree to the{" "}
                            <Anchor href="/account/service-agreement" c="primary" target="_blank">
                                Service Agreement
                            </Anchor>{" "}
                            which governs both the Terms of Service and End User License Agreement.
                        </Text>
                    }
                />

                <Divider mx="-sm" />

                <Group justify="flex-end">
                    <Button
                        disabled={!(form.getValues().agreedToPrivacyPolicy && form.getValues().agreedToTOS)}
                        rightSection={<IconChevronRight />}
                        type="submit"
                        onClick={(e) => {
                            e.preventDefault();
                            if (form.validate().hasErrors) return;
                            setStep(step + 1);
                        }}
                    >
                        Continue
                    </Button>
                </Group>
            </Stack>
        </form>
    );
};
